/**
 * Compras registradas como TICKET COMPLETO.
 *
 * La usuaria compra en supermercados y Costco: un solo ticket con varios
 * productos. Obligarla a crear una compra por producto seria multiplicar el
 * trabajo de captura, asi que el modelo y la pantalla giran alrededor del
 * ticket (Req. 6.1).
 *
 * Al guardar, cada renglon actualiza el inventario y el costo promedio
 * ponderado de su insumo, todo dentro de UNA transaccion: si algo falla, no
 * queda medio registrado (Req. 6.5, 1.4).
 */

import { db } from '../schema';
import { enEscritura } from '../tx';
import { nuevoId } from '../ids';
import {
  exigirCantidadPositiva,
  exigirImporteNoNegativo,
  exigirFecha,
  textoOpcional,
} from '../validation';
import { ErrorValidacion } from '@/domain/errors';
import { ahoraIso, hoy, type FechaLocal } from '@/domain/dates';
import { aBase, formatearCantidadBase } from '@/domain/units';
import { aplicarEntradaValuada, type EstadoInventario } from '@/domain/inventory';
import { formatearCentavos, type Centavos } from '@/domain/money';
import type { Ingredient, Purchase, PurchaseItem, InventoryMovement } from '../types';
import { contextoDe, estadoDe, construirMovimiento } from './ingredients';

export interface RenglonNuevaCompra {
  ingredientId: string;
  /** Cantidad tal como viene en el ticket. */
  cantidadCaptura: number;
  unidadCaptura: string;
  /** Precio del renglon completo, no por unidad. */
  costoCentavos: Centavos;
}

export interface DatosNuevaCompra {
  fecha?: FechaLocal;
  proveedor?: string | null;
  notas?: string | null;
  renglones: RenglonNuevaCompra[];
}

export interface ResultadoCompra {
  purchaseId: string;
  totalCentavos: Centavos;
  renglones: number;
  advertencias: string[];
}

/**
 * Registra un ticket completo.
 *
 * Todo se valida ANTES de abrir la transaccion: rechazar un dato mal formado en
 * la frontera es mas barato que descubrirlo cuando ya contamino el inventario.
 */
export async function registrarCompra(datos: DatosNuevaCompra): Promise<ResultadoCompra> {
  const fecha = exigirFecha(datos.fecha ?? hoy(), 'fecha');
  const proveedor = textoOpcional(datos.proveedor, 'proveedor', 120);
  const notas = textoOpcional(datos.notas, 'notas');

  if (datos.renglones.length === 0) {
    throw new ErrorValidacion('La compra necesita al menos un producto.');
  }

  // --- Validacion y conversion de cada renglon ---------------------------
  const insumos = new Map<string, Ingredient>();
  const preparados: Array<{
    renglon: RenglonNuevaCompra;
    insumo: Ingredient;
    cantidadBase: number;
    cantidadCaptura: number;
    costoCentavos: Centavos;
  }> = [];

  for (const [indice, renglon] of datos.renglones.entries()) {
    const etiqueta = `producto ${indice + 1}`;
    const cantidad = exigirCantidadPositiva(renglon.cantidadCaptura, `cantidad del ${etiqueta}`);
    const costo = exigirImporteNoNegativo(renglon.costoCentavos, `precio del ${etiqueta}`);

    let insumo = insumos.get(renglon.ingredientId);
    if (insumo === undefined) {
      const encontrado = await db.ingredients.get(renglon.ingredientId);
      if (encontrado === undefined) {
        throw new ErrorValidacion(
          `No se encontro el insumo del ${etiqueta}. Guarda el insumo antes de agregarlo al ticket.`,
        );
      }
      insumo = encontrado;
      insumos.set(insumo.id, insumo);
    }

    // Lanza error de conversion si la unidad no corresponde al insumo
    const cantidadBase = aBase(cantidad, renglon.unidadCaptura, contextoDe(insumo));

    preparados.push({
      renglon,
      insumo,
      cantidadBase,
      cantidadCaptura: cantidad,
      costoCentavos: costo,
    });
  }

  const totalCentavos = preparados.reduce((suma, p) => suma + p.costoCentavos, 0);

  // --- Aplicacion del inventario en memoria ------------------------------
  //
  // El estado se acumula por insumo antes de escribir. Asi, si el mismo
  // producto aparece dos veces en el ticket (algo normal en Costco), el segundo
  // renglon parte del estado que dejo el primero y el promedio sale correcto.
  const estados = new Map<string, EstadoInventario>();
  const advertencias: string[] = [];
  const movimientos: InventoryMovement[] = [];
  const renglonesGuardar: PurchaseItem[] = [];

  const purchaseId = nuevoId('com');

  for (const p of preparados) {
    const estadoPrevio = estados.get(p.insumo.id) ?? estadoDe(p.insumo);
    const resultado = aplicarEntradaValuada(estadoPrevio, p.cantidadBase, p.costoCentavos);
    estados.set(p.insumo.id, resultado.estado);

    for (const aviso of resultado.advertencias) {
      advertencias.push(`${p.insumo.nombre}: ${aviso}`);
    }

    renglonesGuardar.push({
      id: nuevoId('comit'),
      purchaseId,
      ingredientId: p.insumo.id,
      cantidadCaptura: p.cantidadCaptura,
      unidadCaptura: p.renglon.unidadCaptura,
      cantidadBase: p.cantidadBase,
      costoCentavos: p.costoCentavos,
    });

    movimientos.push(
      construirMovimiento({
        ingredientId: p.insumo.id,
        tipo: 'compra',
        fecha,
        deltas: resultado.deltas,
        refTipo: 'purchase',
        refId: purchaseId,
        motivo: null,
      }),
    );
  }

  const compra: Purchase = {
    id: purchaseId,
    fecha,
    proveedor,
    totalCentavos,
    notas,
    anuladaPor: null,
    creadoEn: ahoraIso(),
  };

  // --- Escritura atomica -------------------------------------------------
  await enEscritura(
    [db.purchases, db.purchaseItems, db.ingredients, db.inventoryMovements],
    async () => {
      await db.purchases.add(compra);
      await db.purchaseItems.bulkAdd(renglonesGuardar);
      await db.inventoryMovements.bulkAdd(movimientos);

      for (const [ingredientId, estado] of estados) {
        await db.ingredients.update(ingredientId, estado);
      }
    },
  );

  return {
    purchaseId,
    totalCentavos,
    renglones: renglonesGuardar.length,
    advertencias,
  };
}

// ---------------------------------------------------------------------------
// Consultas
// ---------------------------------------------------------------------------

export interface RenglonConInsumo extends PurchaseItem {
  nombreInsumo: string;
  cantidadLegible: string;
  costoPorUnidadCaptura: Centavos;
}

export interface CompraConRenglones extends Purchase {
  renglones: RenglonConInsumo[];
}

/** Historial de compras, de la mas reciente a la mas antigua. */
export async function listarCompras(limite = 50): Promise<CompraConRenglones[]> {
  const compras = await db.purchases.orderBy('fecha').reverse().limit(limite).toArray();
  if (compras.length === 0) return [];

  const ids = compras.map((c) => c.id);
  const renglones = await db.purchaseItems.where('purchaseId').anyOf(ids).toArray();
  const insumos = new Map((await db.ingredients.toArray()).map((i) => [i.id, i]));

  return compras.map((compra) => ({
    ...compra,
    renglones: renglones
      .filter((r) => r.purchaseId === compra.id)
      .map((r) => enriquecer(r, insumos.get(r.ingredientId))),
  }));
}

export async function obtenerCompra(id: string): Promise<CompraConRenglones | undefined> {
  const compra = await db.purchases.get(id);
  if (compra === undefined) return undefined;

  const renglones = await db.purchaseItems.where('purchaseId').equals(id).toArray();
  const insumos = new Map((await db.ingredients.toArray()).map((i) => [i.id, i]));

  return {
    ...compra,
    renglones: renglones.map((r) => enriquecer(r, insumos.get(r.ingredientId))),
  };
}

function enriquecer(renglon: PurchaseItem, insumo: Ingredient | undefined): RenglonConInsumo {
  return {
    ...renglon,
    nombreInsumo: insumo?.nombre ?? '(insumo eliminado)',
    cantidadLegible:
      insumo === undefined
        ? String(renglon.cantidadBase)
        : formatearCantidadBase(
            renglon.cantidadBase,
            insumo.dimension,
            insumo.unidadCompraPreferida,
          ),
    costoPorUnidadCaptura:
      renglon.cantidadCaptura > 0
        ? Math.round(renglon.costoCentavos / renglon.cantidadCaptura)
        : 0,
  };
}

/** Proveedores ya usados, para sugerirlos en lugar de escribirlos cada vez. */
export async function proveedoresUsados(limite = 10): Promise<string[]> {
  const compras = await db.purchases.toArray();
  const conteo = new Map<string, number>();

  for (const compra of compras) {
    if (compra.proveedor === null || compra.proveedor === '') continue;
    conteo.set(compra.proveedor, (conteo.get(compra.proveedor) ?? 0) + 1);
  }

  return [...conteo.entries()]
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0], 'es'))
    .slice(0, limite)
    .map(([nombre]) => nombre);
}

/** Costo por unidad de un renglon, para mostrarlo en vivo durante la captura. */
export function costoPorUnidad(costoCentavos: Centavos, cantidad: number): string {
  if (cantidad <= 0) return '—';
  return formatearCentavos(Math.round(costoCentavos / cantidad));
}
